import {
	Block,
	Card,
	Button,
	Icon,
	Label,
	Input,
	OutsideClick,
	Loader,
	Text,
	Row,
	Column,
	Checkbox,
	Tooltip,
	Flex,
	IconName
} from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useState, useRef } from 'react';
import T from 'Components/Helpers/translate';
import { SlideFade } from 'App/components/animations';
import Client from 'App/resources/Model/Client';
import Ticket from 'App/resources/Model/Ticket';
import logError from 'App/babel/helpers/logError';
import Relation from 'App/resources/Model/EntityRelation';
import { fetchTickets } from 'App/components/RelationSelect/helpers/fetchHelpers';
import { ticketRelationMapper } from 'App/components/RelationSelect/helpers/relationMappers';
import UserDefinedObjectResource from 'App/resources/UserDefinedObject';
import { UserDefinedObjectTypeId } from 'App/resources/Model/UserDefinedObjectType';
import { getSelf } from 'Store/selectors/AppSelectors';
import openModal from 'App/services/Modal';

import 'App/components/RelationSelect/RelationSelect.scss';
import 'App/components/RelationSelect/RelationRow/RelationRow.scss';

export type LinkedTicket = {
	id: number;
	title?: string;
	subtitle?: string;
	icon?: IconName;
	status?: { name: string };
};

type UdoObject = {
	id?: number;
	client?: Pick<Client, 'id' | 'name'> | null;
	linkedTickets?: Ticket[];
};

type Props = {
	object: UdoObject;
	typeId: number;
	onPendingTicketsChange?: (ticketIds: number[]) => void;
};

const RelateToTicket = ({ object, typeId, onPendingTicketsChange }: Props) => {
	const classes = new BemClass('RelationSelect');
	const mainRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [isFirstFetch, setIsFirstFetch] = useState(true);
	const [searching, setSearching] = useState(false);
	const [search, setSearch] = useState('');
	const [isUpdating, setIsUpdating] = useState(false);

	const [ticketRelations, setTicketRelations] = useState<Relation[]>([]);
	const [linkedTickets, setLinkedTickets] = useState<LinkedTicket[]>([]);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [unlinkIds, setUnlinkIds] = useState<number[]>([]);

	const self = getSelf();
	const userId = self?.id || 0;
	const clientId = object?.client?.id;
	const objectId = object?.id;
	const isCreatingUdo = !objectId;

	useEffect(() => {
		const existingTickets = object?.linkedTickets || [];
		const mappedTickets: LinkedTicket[] = existingTickets.map(ticket => {
			const mapped = ticketRelationMapper(ticket, userId);
			return {
				id: ticket.id,
				title: mapped?.title ?? ticket.title,
				subtitle: mapped?.subtitle,
				icon: mapped?.icon
			};
		});
		setLinkedTickets(mappedTickets);
	}, [object?.linkedTickets, userId]);

	useEffect(() => {
		if (!clientId) return;

		let cancelled = false;
		let debounce: ReturnType<typeof setTimeout> | null = null;

		setSearching(true);
		debounce = setTimeout(async () => {
			try {
				const tickets = await fetchTickets(search, userId, clientId, false);
				if (!cancelled) {
					const mappedTickets = tickets
						.map(ticket => ticketRelationMapper(ticket, userId))
						.filter(Boolean) as Relation[];
					setTicketRelations(mappedTickets);
					if (isFirstFetch) {
						setIsFirstFetch(false);
					}
				}
			} catch (err) {
				if (!cancelled) {
					logError(err, 'Failed to fetch tickets');
					setTicketRelations([]);
				}
			} finally {
				if (!cancelled) {
					setSearching(false);
				}
			}
		}, 300);

		return () => {
			cancelled = true;
			if (debounce) {
				clearTimeout(debounce);
			}
		};
	}, [clientId, search, userId, isFirstFetch]);

	const isTicketLinked = (ticketId: number) => linkedTickets.some(ticket => ticket.id === ticketId);

	const handleTicketCheckboxToggle = (ticketId: number) => {
		if (isTicketLinked(ticketId)) {
			setUnlinkIds(prev => (prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]));
		} else {
			setSelectedIds(prev =>
				prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]
			);
		}
	};

	const handleTicketSave = async () => {
		if (isCreatingUdo) {
			const toLink: LinkedTicket[] = selectedIds.reduce<LinkedTicket[]>((acc, id) => {
				const ticket = ticketRelations.find(relation => relation.id === id);
				if (ticket) {
					acc.push({ id, title: ticket.title, subtitle: ticket.subtitle, icon: ticket.icon });
				}
				return acc;
			}, []);

			const updatedLinkedTickets = [...linkedTickets, ...toLink];
			setLinkedTickets(updatedLinkedTickets);

			const allLinkedIds = updatedLinkedTickets.map(t => t.id);
			onPendingTicketsChange?.(allLinkedIds);

			setSelectedIds([]);
			setSearch('');
			setOpen(false);
			return;
		}

		if (!objectId) {
			return;
		}

		setIsUpdating(true);
		try {
			if (unlinkIds.length > 0) {
				await Promise.all(
					unlinkIds.map(ticketId =>
						UserDefinedObjectResource.unlinkTicket(typeId as UserDefinedObjectTypeId, objectId, ticketId)
					)
				);
			}

			if (selectedIds.length > 0) {
				await Promise.all(
					selectedIds.map(ticketId =>
						UserDefinedObjectResource.linkTicket(typeId as UserDefinedObjectTypeId, objectId, ticketId)
					)
				);
			}

			const toLink: LinkedTicket[] = selectedIds.reduce<LinkedTicket[]>((acc, id) => {
				const ticket = ticketRelations.find(relation => relation.id === id);
				if (ticket) {
					acc.push({ id, title: ticket.title, subtitle: ticket.subtitle, icon: ticket.icon });
				}
				return acc;
			}, []);

			const updatedLinkedTickets = [...linkedTickets.filter(ticket => !unlinkIds.includes(ticket.id)), ...toLink];
			setLinkedTickets(updatedLinkedTickets);

			setSelectedIds([]);
			setUnlinkIds([]);
			setSearch('');
			setOpen(false);
		} catch (err) {
			logError(err, 'Failed to update ticket links');
		} finally {
			setIsUpdating(false);
		}
	};

	const handleTicketRemove = async (ticketId: number) => {
		if (isCreatingUdo) {
			const updatedLinkedTickets = linkedTickets.filter(ticket => ticket.id !== ticketId);
			setLinkedTickets(updatedLinkedTickets);

			const allLinkedIds = updatedLinkedTickets.map(t => t.id);
			onPendingTicketsChange?.(allLinkedIds);
			return;
		}

		if (!objectId) {
			return;
		}

		setIsUpdating(true);
		try {
			await UserDefinedObjectResource.unlinkTicket(typeId as UserDefinedObjectTypeId, objectId, ticketId);
			setLinkedTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
		} catch (err) {
			logError(err, 'Failed to unlink ticket');
		} finally {
			setIsUpdating(false);
		}
	};

	const hasTicketChanges = selectedIds.length > 0 || unlinkIds.length > 0;
	const disabled = isUpdating;

	if (!clientId) {
		return null;
	}

	return (
		<Flex direction="column" className={classes.b()}>
			<Label>{T('default.relateToTickets')}</Label>
			<OutsideClick
				targetRef={() => mainRef.current}
				listen={open}
				outsideClick={() => {
					setOpen(false);
					setSearch('');
					setSelectedIds([]);
					setUnlinkIds([]);
				}}
			>
				<div className={classes.elem('content').b()} ref={mainRef} style={{ position: 'relative' }}>
					<SlideFade visible direction="top">
						<Input
							className={classes.elem('input').b()}
							icon="search"
							placeholder={T('default.search')}
							value={search}
							onFocus={() => setOpen(true)}
							onChange={e => setSearch(e.target.value)}
							disabled={disabled}
						/>
					</SlideFade>

					<SlideFade direction="top" bounce visible={open}>
						<Card className={classes.elem('dropdown').b()}>
							<Block className={classes.elem('card').b()}>
								{searching ? <Loader size="sm" /> : null}
								{!searching && ticketRelations.length === 0 ? (
									<Row className="RelationRow">
										<Column>
											<Text italic color="grey-11" align="center">
												{T('noResult.generic')}
											</Text>
										</Column>
									</Row>
								) : null}
								{!searching &&
									ticketRelations.map(ticket => (
										<Row
											key={ticket.id}
											className="RelationRow"
											onClick={() => handleTicketCheckboxToggle(ticket.id)}
										>
											<Column fixedWidth={32} align="center">
												<Checkbox
													size="sm"
													checked={
														(isTicketLinked(ticket.id) && !unlinkIds.includes(ticket.id)) ||
														selectedIds.includes(ticket.id)
													}
													disabled={disabled}
												/>
											</Column>
											<Column fixedWidth={50} align="center">
												<Icon name={ticket.icon || 'customer-support'} />
											</Column>
											<Column>
												<Text ellipsis>{ticket.title}</Text>
												{ticket.subtitle ? (
													<Text size="sm" color="grey-11" ellipsis>
														{ticket.subtitle}
													</Text>
												) : null}
											</Column>
										</Row>
									))}
							</Block>
							<Button
								block
								color={hasTicketChanges ? 'green' : 'super-light-green'}
								onClick={() => {
									if (hasTicketChanges) {
										handleTicketSave();
									} else {
										setOpen(false);
										setSearch('');
									}
								}}
								disabled={disabled}
							>
								<Icon name={hasTicketChanges ? 'check' : 'times'} spacing="mr2" />
								{hasTicketChanges ? T('default.save') : T('todo.noNotRelated')}
							</Button>
						</Card>
					</SlideFade>

					{linkedTickets.length > 0 ? (
						<Block space="mtm">
							{linkedTickets.map(ticket => (
								<Row
									key={ticket.id}
									className="RelationRow"
									onClick={() => openModal('EditTicket', { ticketId: ticket.id })}
								>
									<Column fixedWidth={50} align="center">
										<Icon name={ticket.icon || 'customer-support'} />
									</Column>
									<Column>
										<Text>{ticket.title || `${T('default.ticket')} #${ticket.id}`}</Text>
										{ticket.subtitle ? (
											<Text size="sm" color="grey-11" ellipsis>
												{ticket.subtitle}
											</Text>
										) : null}
									</Column>
									<Column fixedWidth={40} align="center">
										<Tooltip title={T('client.removeRelation')}>
											<Icon
												name="times"
												onClick={e => {
													e.stopPropagation();
													if (!disabled) {
														handleTicketRemove(ticket.id);
													}
												}}
											/>
										</Tooltip>
									</Column>
								</Row>
							))}
						</Block>
					) : null}
				</div>
			</OutsideClick>
		</Flex>
	);
};

export default RelateToTicket;
